import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../lib/api";

/**
 * Hook reutilizável de paginação server-side.
 * Suporta endpoints que retornam {items, total, limit, offset} (passa paginated=true).
 *
 * @param {string} endpoint  Ex: "/clientes", "/unidades", "/contratos"
 * @param {object} options
 *   - limite (default 20)
 *   - filtrosIniciais (default {})
 *   - debounceMs (default 300, aplicado a filtros tipo string >=3 chars)
 */
export function usePaginacao(endpoint, { limite = 20, filtrosIniciais = {}, debounceMs = 300 } = {}) {
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [filtros, setFiltros] = useState(filtrosIniciais);

  const totalPaginas = Math.max(1, Math.ceil(total / limite));
  const debounceRef = useRef(null);

  const buscar = useCallback(async (paginaAtual, filtrosAtuais) => {
    setCarregando(true);
    setErro(null);
    try {
      const offset = (paginaAtual - 1) * limite;
      const params = {
        limit: limite,
        offset,
        paginated: true,
        ...Object.fromEntries(
          Object.entries(filtrosAtuais || {}).filter(([, v]) => v !== "" && v != null && v !== "all")
        ),
      };
      const r = await api.get(endpoint, { params });
      const data = r.data;
      if (Array.isArray(data)) {
        setItens(data);
        setTotal(data.length);
      } else {
        setItens(data.items ?? data.data ?? []);
        setTotal(data.total ?? 0);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[usePaginacao ${endpoint}]`, err?.response?.status, err?.response?.data);
      setErro(err?.response?.data?.detail || "Erro ao carregar dados");
      setItens([]);
      setTotal(0);
    } finally {
      setCarregando(false);
    }
  }, [endpoint, limite]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => buscar(pagina, filtros), debounceMs);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagina, JSON.stringify(filtros)]);

  const irParaPagina = (n) => {
    if (n < 1 || n > totalPaginas) return;
    setPagina(n);
  };

  const atualizarFiltros = (novosFiltros) => {
    setFiltros((f) => ({ ...f, ...novosFiltros }));
    setPagina(1);
  };

  const setFiltro = (chave, valor) => atualizarFiltros({ [chave]: valor });

  return {
    itens, total, pagina, totalPaginas, limite,
    carregando, erro, filtros,
    irParaPagina, atualizarFiltros, setFiltro,
    recarregar: () => buscar(pagina, filtros),
  };
}
